/**
 * Study-centre learner vocabulary for the intake wizard + pathway list
 * (Phase SC-3b). Deliberately de-schooled: no "class", "homework",
 * "exam hall", "period" — a study-centre learner is prepping for a goal
 * (an exam, a subject, a skill) or reading for pleasure. Nothing here
 * references school or coaching concepts (vertical-isolation, Iron Rule
 * on no cross-vertical leaks).
 */
import {
  BookOpen,
  GraduationCap,
  Lightbulb,
  Sparkles,
  Target,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import type { BookSource, LearningPurpose, PathwayStatus, RoadmapSource } from "./types";

export type { BookSource, LearningPurpose, PathwayStatus, RoadmapSource } from "./types";

export interface PurposeOption {
  value: LearningPurpose;
  label: string;
  description: string;
  icon: LucideIcon;
}

/** Purpose cards, in the order the learner sees them on step 1. */
export const PURPOSE_OPTIONS: PurposeOption[] = [
  {
    value: "exam_prep",
    label: "Crack an exam",
    description: "UPSC, SSC, NEET, JEE, CAT — anything you're preparing for.",
    icon: Target,
  },
  {
    value: "concept_mastery",
    label: "Master a subject",
    description: "Go deep on a subject and truly understand it.",
    icon: Lightbulb,
  },
  {
    value: "skill_building",
    label: "Build a skill",
    description: "Coding, a language, design — a skill you want to grow.",
    icon: Wrench,
  },
  {
    value: "school_support",
    label: "Stay ahead in school",
    description: "Revise alongside what you're learning right now.",
    icon: GraduationCap,
  },
  {
    value: "casual_reading",
    label: "Read for pleasure",
    description: "No plan, no pressure — just great books to enjoy.",
    icon: BookOpen,
  },
  {
    value: "other",
    label: "Something else",
    description: "Tell us what you're here to work on.",
    icon: Sparkles,
  },
];

export const PURPOSE_LABEL: Record<LearningPurpose, string> = Object.fromEntries(
  PURPOSE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<LearningPurpose, string>;

export const STATUS_LABEL: Record<PathwayStatus, string> = {
  active: "Active",
  paused: "Paused",
  completed: "Completed",
  archived: "Archived",
};

export const ROADMAP_LABEL: Record<RoadmapSource, string> = {
  ai_generated: "Build me a roadmap",
  self_chosen: "I have my own plan",
};

export const BOOK_LABEL: Record<BookSource, string> = {
  ai_pick: "Suggest the best books",
  self_specified: "I'll pick my own book",
};

/** Casual readers get book suggestions but never a day-by-day roadmap. */
export function purposeWantsRoadmap(purpose: LearningPurpose): boolean {
  return purpose !== "casual_reading";
}

/** A pace budget (days + daily minutes) only makes sense for goal-driven plans. */
export function purposeWantsPace(purpose: LearningPurpose): boolean {
  return purpose !== "casual_reading";
}

/** Only exam prep asks for a named exam. */
export function purposeWantsExam(purpose: LearningPurpose): boolean {
  return purpose === "exam_prep";
}

/** Read-for-pleasure plans: log reading only — no SR, quizzes, or reports. */
export function isReadForPleasurePlan(purpose: LearningPurpose): boolean {
  return purpose === "casual_reading";
}

/** The chip-input's label + placeholder shifts with the purpose. */
export function subjectsCopy(purpose: LearningPurpose): { label: string; placeholder: string } {
  if (purpose === "casual_reading") {
    return { label: "Genres you enjoy", placeholder: "e.g. Sci-fi, Biography" };
  }
  if (purpose === "skill_building") {
    return { label: "Skills or areas", placeholder: "e.g. Python, Public speaking" };
  }
  return { label: "Subjects", placeholder: "e.g. Polity, Economics" };
}

/**
 * A sensible default pathway title so the learner isn't forced to name it
 * up front — editable on the details step. Derives from the exam name or
 * the first subject when present, else the purpose label.
 */
export function defaultTitleFor(
  purpose: LearningPurpose,
  opts: { examName?: string | null; subjects?: string[] } = {},
): string {
  const exam = opts.examName?.trim();
  if (purpose === "exam_prep" && exam) return `${exam} preparation`;
  const firstSubject = opts.subjects?.find((s) => s.trim().length > 0)?.trim();
  if (firstSubject) {
    if (purpose === "casual_reading") return `Reading: ${firstSubject}`;
    if (purpose === "skill_building") return `Learning ${firstSubject}`;
    return `${firstSubject}`;
  }
  return PURPOSE_LABEL[purpose];
}
